'use client';
import { useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useDebug } from '@shared/debug';
import { useFetchScratchcards } from '@shared/hooks';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { PromotionsContent, PromotionsContentSkeleton, PromotionsEmpty, PromotionsList } from '../components';
import type { BonusOfferData } from '../types/types';
import { getPromotionUrls } from '../utils/promotionApiMapper';
import { mapBonus } from './utils/mapBonus/mapBonus';

type PromoContentBonusFundsProps = ComponentProps<'div'> & {
  namespace: string;
  filteredVertical?: string[];
};

const PROMOTION_TYPE = 'bonuses' as const;

/**
 * PromoContentBonusFunds displays a list of available and previous bonus fund promotions.
 *
 * Fetches active and non-active bonus funds using the API, maps the data for display,
 * and renders the promotions content with skeleton loading and logs any errors into console..
 *
 * @component
 * @param {string} props.namespace - The translation namespace to use for i18n.
 * @returns {JSX.Element} The rendered promotions content for bonus funds.
 */
function PromoContentBonusFunds({ namespace, filteredVertical, ...props }: PromoContentBonusFundsProps) {
  const {
    data: availableBonus,
    isLoading: isLoadingAvailable,
    error: errorAvailable,
  } = useApiQuery<BonusOfferData>(getPromotionUrls('bonuses', { status: 'active', offset: '0', limit: '10', filteredVertical }).list, {
    credentials: true,
    queryOptions: { staleTime: 0 },
  });
  const {
    data: previousBonus,
    isLoading: isLoadingPrevious,
    error: errorPrevious,
  } = useApiQuery<BonusOfferData>(getPromotionUrls('bonuses', { status: 'non-active', offset: '0', limit: '5', filteredVertical: [] }).list, {
    credentials: true,
    queryOptions: { staleTime: 0 },
  });
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';

  const t = useTranslations(namespace);
  const debug = useDebug();

  const hasScratchcards = availableBonus?.list?.some(item => item.bonusProductTypes?.includes('SCRATCHCARDS'));
  const { scratchcards, isLoading: isLoadingScratchcards } = useFetchScratchcards({
    enabled: isLoggedIn && hasScratchcards,
  });

  if (isLoadingAvailable || isLoadingPrevious || isLoadingScratchcards) return <PromotionsContentSkeleton className={props.className} />;
  if (errorAvailable || errorPrevious) debug.error(`Fetching failed in PromoContentBonusFunds. ${errorAvailable || errorPrevious}`);

  const mappedAvailableBonus = availableBonus?.list?.map(item => mapBonus({ item, t, scratchcards })) ?? [];
  const mappedPreviousBonus = previousBonus?.list?.map(item => mapBonus({ item, t, isPrevious: true })) ?? [];

  return (
    <PromotionsContent promoType={PROMOTION_TYPE} {...props}>
      {mappedAvailableBonus?.length ? <PromotionsList promoType={PROMOTION_TYPE} data={mappedAvailableBonus} /> : <PromotionsEmpty promoType={PROMOTION_TYPE} />}
      {mappedPreviousBonus?.length ? <PromotionsList promoType={PROMOTION_TYPE} data={mappedPreviousBonus} showTitle grayscale /> : null}
    </PromotionsContent>
  );
}

export { PromoContentBonusFunds };
